export const statusText = {
    analyzing: 'Analyzing',
    invalid_documents: 'Invalid Documents',
    created: 'Link Sent',
    candidate_initiated: 'Candidate Initiated',
    link_expired: 'Link Expired',
    pending: 'TruCheck Review',
    pre_adverse_action: 'Pre-Adverse Action',
    ready_for_adjudication: 'Ready for Adjudication',
    review_required: 'Review Required',
    clear: 'Clear',
    completed: 'Completed',
    canceled: 'Canceled',
    rejected: 'Rejected',
    eligible: 'Eligible',
    not_eligible: 'Not Eligible',
}

export const statusBadge = {
    analyzing: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    invalid_documents: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    created: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
    candidate_initiated: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200',
    link_expired: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
    pending: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    pre_adverse_action: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
    ready_for_adjudication: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    review_required: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    clear: 'bg-green-50 text-green-700 ring-1 ring-green-200',
    completed: 'bg-green-50 text-green-700 ring-1 ring-green-200',
    canceled: 'bg-gray-100 text-gray-600 ring-1 ring-gray-300',
    rejected: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    eligible: 'bg-green-50 text-green-700 ring-1 ring-green-200',
    not_eligible: 'bg-red-50 text-red-700 ring-1 ring-red-200',
}

export const statusDot = {
    analyzing: 'bg-amber-500',
    invalid_documents: 'bg-red-500',
    created: 'bg-blue-500',
    candidate_initiated: 'bg-cyan-500',
    link_expired: 'bg-rose-500',
    pending: 'bg-amber-500',
    pre_adverse_action: 'bg-indigo-500',
    ready_for_adjudication: 'bg-amber-500',
    review_required: 'bg-red-500',
    clear: 'bg-green-500',
    completed: 'bg-green-500',
    canceled: 'bg-gray-400',
    rejected: 'bg-red-500',
    eligible: 'bg-green-500',
    not_eligible: 'bg-red-500',
}

// Who the TruCheck is "waiting on" at each stage — shown as a tooltip on the Stage badge so
// it's clear whose action is needed next.
export const statusActor = {
    created: 'Candidate — completing the verification',
    candidate_initiated: 'Candidate — filling out the form',
    link_expired: 'Client — resend the expired invitation link',
    analyzing: 'System — analyzing the submitted documents',
    pending: 'QA team — reviewing the submission',
    pre_adverse_action: 'Candidate — responding to the pre-adverse action notice',
    ready_for_adjudication: 'Client — make the adjudication decision',
    invalid_documents: 'Client — review the create-time documents',
    completed: 'Completed — no action needed',
    canceled: 'Canceled — no action needed',
    rejected: 'Rejected — no action needed',
}

export const SortArrow = ({ column, sortConfig }) => {
    const isActive = sortConfig.key === column
    const isAsc = isActive && sortConfig.direction === 'asc'

    return (
        <span className={`inline-flex flex-col ml-1 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
            <svg
                className={`w-2.5 h-2.5 -mb-0.5 ${isAsc ? 'opacity-100' : 'opacity-30'}`}
                viewBox="0 0 10 6"
                fill="currentColor"
            >
                <path d="M5 0L10 6H0L5 0Z" />
            </svg>

            <svg
                className={`w-2.5 h-2.5 ${!isAsc ? 'opacity-100' : 'opacity-30'}`}
                viewBox="0 0 10 6"
                fill="currentColor"
            >
                <path d="M5 6L0 0H10L5 6Z" />
            </svg>
        </span>
    )
}

// The TruCheck is still awaiting the candidate — the link was sent but not yet submitted.
// "Link Expired" is the same underlying backend state (status "created", no submittedTime),
// just past its completion window, so it stays eligible for the same HR actions (resend /
// cancel). Resending mints a fresh link AND resets the expiry, so an expired link is
// recoverable.
export const isAwaitingCandidate = (effectiveStatus) =>
    effectiveStatus === 'created' || effectiveStatus === 'candidate_initiated' || effectiveStatus === 'link_expired'

// The backend `status` IS the merged lifecycle stage now (one of created | pending |
// ready_for_adjudication | completed | pre_adverse_action | link_expired | invalid_documents |
// canceled | rejected), set hard on every transition — so the effective status is just `status`.
// Kept as a helper (rather than inlining `job.status`) so the many call sites that build an
// `effectiveStatus` field have one place to change if that ever needs adjusting.
export const getEffectiveStatus = (job) => job?.status

// The statuses that mean "the automated + QA pipeline has finished, so results exist" — i.e. the old
// raw `status === "completed"` anchor, which since the status/stage merge is spread across these
// values. Use `hasReachedCompletion` instead of comparing to 'completed' (which now means specifically
// "the client has made a decision") for gates like "can view the consumer report / make a decision".
export const COMPLETED_FAMILY_STATUSES = ['ready_for_adjudication', 'pre_adverse_action', 'completed']

export const hasReachedCompletion = (job) =>
    COMPLETED_FAMILY_STATUSES.includes(getEffectiveStatus(job))
export const resultDot = {
    review_required: 'bg-red-500',
    clear: 'bg-green-500',
}

export const resultText = {
    review_required: 'Review Required',
    clear: 'Clear',
}

export const resultBadge = {
    review_required: 'bg-red-50 text-red-700',
    clear: 'bg-green-50 text-green-700',
}

